import { createModel } from '@rematch/core';
import { TASK_TYPE_MAP } from 'constants/pipeline';
import { post } from 'utils/http';
import { uploadChunksInParallel } from 'utils/cosUpload';
import type {
  UploadProgressCallback,
  IExtractTaskStatus,
  IConfirmUploadRequest,
  IInitDirectUploadResponse,
  ICompleteDirectUploadResponse,
} from 'types/pipeline';
import { safeParseJson } from 'utils/tool';

export default createModel()({
  state: {
    cleanModuleList: [], // 清洗模板列表
    crawlModuleList: [], // 抓取模板列表
    corpusSetList: [], // 语料集列表
  },
  reducers: {
    setState: (state: any, payload: any) => ({ ...state, ...payload }),
  },
  effects: {
    setReduxData({ key, data }) {
      this.setState({
        [key]: data,
      });
    },
    setQueryParams(data) {
      this.setState({
        queryParams: data,
      });
    },
    // 拉取任务列表
    async getPipelineTaskList(data = {}) {
      const rst = await post('/api/pipeline/getPipelineTaskList', data);
      return rst || {};
    },
    // 拉取语料集列表
    async getCorpusSetList(data = {}) {
      const rst = await post('/api/pipeline/getCorpusSetList', data);
      return rst || {};
    },
    // 拉取语料集数据
    async getCorpusSetItems(data = {}) {
      const rst = await post('/api/pipeline/getCorpusSetItems', data);
      return rst || {};
    },
    // 获取任务可配置模板
    async getTaskModuleTemplate(data = {}) {
      const rst = await post('/api/pipeline/getTaskModuleTemplate', data);
      if (rst) {
        const { taskType } = data;

        const { corpusSetList, taskModuleTmpls } = rst;
        // 根据不同的任务类型保存不同的数据
        if (taskType === TASK_TYPE_MAP.CRAWL.value) {
          // 抓取任务：只保存抓取模板列表
          this.setState({
            crawlModuleList: taskModuleTmpls?.crawlTmpls || [],
          });
        } else if (taskType === TASK_TYPE_MAP.CLEAN.value) {
          // 清洗任务：保存清洗模板列表和语料集列表
          this.setState({
            cleanModuleList: taskModuleTmpls?.cleanTmpls || [],
            corpusSetList: corpusSetList || [],
          });
        } else if (taskType === TASK_TYPE_MAP.MACHINE_MODIFY.value) {
          // 修改任务：只保存修改模板列表
          this.setState({
            modifyModuleList: taskModuleTmpls?.machineModifyTmpls || [],
            corpusSetList: corpusSetList || [],
          });
        } else if (taskType === TASK_TYPE_MAP.MACHINE_COMMON.value) {
          // 处理链路：保存语料集列表
          this.setState({
            corpusSetList: corpusSetList || [],
          });
        }
      }
      return rst || {};
    },
    // 获取任务详情
    async getTaskDetail(data = {}) {
      const rst = await post('/api/pipeline/getTaskDetail', data);
      return rst || {};
    },
    // 下载语料集数据
    async downloadCorpusSet(data = {}) {
      const rst = await post('/api/pipeline/downloadCorpusSet', data);
      return rst || {};
    },
    // 创建|更新任务
    async taskCreateOrUpdate(data = {}) {
      const rst = await post('/api/pipeline/taskCreateOrUpdate', data);
      return rst || {};
    },
    // 任务试运行
    async taskTrailRun(data = {}) {
      const rst = await post('/api/pipeline/taskTrailRun', data);
      return rst || {};
    },
    // 任务运行
    async taskRun(data = {}) {
      const rst = await post('/api/pipeline/taskRun', data);
      return rst || {};
    },
    // 任务停止
    async taskStop(data = {}) {
      const rst = await post('/api/pipeline/taskStop', data);
      return rst || {};
    },
    // 上传原始文件（ZIP/Excel）- COS 直传分片上传
    async uploadOriginalFile({
      file,
      labelData,
      onProgress,
      onUploadIdReady,
      signal,
    }: {
      file: File;
      labelData?: Record<string, Record<string, string>> | null;
      onProgress?: UploadProgressCallback;
      onUploadIdReady?: (uploadId: string) => void;
      signal?: AbortSignal;
    }) {
      const fileSize = file.size;
      const fileName = file.name;

      // 检查是否已取消
      const checkAborted = () => {
        if (signal?.aborted) {
          throw new Error('上传已取消');
        }
      };

      try {
        checkAborted();

        // 预计算分片数量
        const DEFAULT_CHUNK_SIZE = 50 * 1024 * 1024;
        const estimatedChunks = Math.ceil(fileSize / DEFAULT_CHUNK_SIZE);

        // 构建初始化请求参数
        const initParams: {
          fileName: string;
          fileSize: number;
          totalChunks: number;
          labelData?: Record<string, Record<string, string>>;
        } = {
          fileName,
          fileSize,
          totalChunks: estimatedChunks,
        };

        // 如果有标签数据，添加到请求参数中
        if (labelData && Object.keys(labelData).length > 0) {
          initParams.labelData = labelData;
        }

        // 1. 初始化 COS 直传分片上传
        const initResponse: IInitDirectUploadResponse = await post('/api/pipeline/initDirectUpload', initParams, {
          signal,
        });

        const { uploadId, chunkSize, presignedUrls } = initResponse;

        if (!uploadId || !presignedUrls) {
          throw new Error('初始化分片上传失败');
        }

        // 通知上层 uploadId 已就绪（用于取消操作）
        // 注意：后端现在 batchId 和 uploadId 是同一个值
        if (onUploadIdReady) {
          onUploadIdReady(uploadId);
        }

        checkAborted();

        // 2. 并行直传分片到 COS
        if (onProgress) {
          onProgress(0, '开始上传分片...');
        }

        const uploadedParts = await uploadChunksInParallel({
          file,
          chunkSize,
          presignedUrls,
          signal,
          parallelLimit: 3,
          onProgress: (uploadedChunks, total) => {
            if (onProgress) {
              const percent = Math.round((uploadedChunks / total) * 100);
              onProgress(percent, `上传中 ${uploadedChunks}/${total} 片`);
            }
          },
        });

        checkAborted();

        // 3. 通知后端合并分片
        if (onProgress) {
          onProgress(100, '分片上传完成，等待服务器处理...');
        }

        const completeResponse: ICompleteDirectUploadResponse = await post(
          '/api/pipeline/completeDirectUpload',
          {
            uploadId,
            fileName,
            parts: uploadedParts,
          },
          { signal },
        );

        // 后端现在 batchId 和 uploadId 是同一个值
        return { batchId: completeResponse.batchId, uploadId };
      } catch (error) {
        if (signal?.aborted) {
          throw new Error('上传已取消');
        }
        throw new Error(error instanceof Error ? error.message : '分片上传失败');
      }
    },

    // 取消 COS 直传上传任务
    async cancelDirectUpload(uploadId: string) {
      const result = await post('/api/pipeline/cancelDirectUpload', { uploadId });
      return result;
    },

    // 查询上传任务状态
    async getTaskStatus(batchId: string) {
      const result = await post('/api/pipeline/getTaskStatus', { batchId });

      // 解析失败详情 JSON 字符串
      let failedFiles: Array<{ fileName: string; reason: string }> = [];
      let unmatchedLabelCount = 0;
      let unmatchedLabelFiles: string[] = [];

      if (result?.failed_details) {
        const failedDetails = safeParseJson(result.failed_details);
        failedFiles = failedDetails?.details || [];

        // 解析标签匹配失败信息
        if (failedDetails?.unmatchedLabelCount) {
          unmatchedLabelCount = failedDetails.unmatchedLabelCount;
        }
        if (failedDetails?.unmatchedLabelFiles) {
          unmatchedLabelFiles = failedDetails.unmatchedLabelFiles;
        }
      }

      const mapped: IExtractTaskStatus = {
        status: result.status,
        progress: result.progress ?? 0,
        message: result.message ?? '',
        error: result.error_message,
        corpusType: result.corpus_type,
        totalCount: result.total_count,
        successCount: result.success_count,
        failedCount: result.failed_count,
        failedFiles,
        canRetry: result.canRetry ?? false,
        unmatchedLabelCount,
        unmatchedLabelFiles,
      };
      return mapped;
    },

    // 确认上传语料集
    async confirmCorpusUpload(data: IConfirmUploadRequest) {
      const result = await post('/api/pipeline/confirmCorpusUpload', data);
      return result;
    },

    // 清空数据
    async cleanExpiredTasks(batchId: string) {
      const result = await post('/api/pipeline/cleanExpiredTasks', { batchId });
      return result;
    },

    // 分页查询上传历史
    async getUploadCraft({ batchId, pageNum, pageSize }: { batchId: string; pageNum: number; pageSize: number }) {
      const result = await post('/api/pipeline/getUploadCraft', {
        batchId,
        pageNum,
        pageSize,
      });
      return result;
    },

    // 取消上传任务
    async cancelUploadTask({ uploadId }: { uploadId?: string }) {
      const result = await post('/api/pipeline/cancelDirectUpload', { uploadId });
      return result;
    },

    // 任务重试
    async retryDirectUpload(batchId: string) {
      const result = await post('/api/pipeline/retryDirectUpload', { batchId });
      return result;
    },

    // 导出为新语料集
    async exportAsNewCorpusSet(data: {
      itemBizIdList: (number | string)[];
      newSetName: string;
      newSetDesc?: string;
      originSetId?: number | string;
    }) {
      const result = await post('/api/pipeline/exportAsNewCorpusSet', data);
      return result;
    },
    // 创建或更新语料集
    async createOrUpdateCorpusSet(data: {
      id?: number;
      name: string;
      desc?: string;
      corpusItemType: number;
      corpusSetType: number;
    }) {
      const result = await post('/api/pipeline/createOrUpdateCorpusSet', data);
      return result;
    },
  },
});
